<script>
import _ from 'lodash'
import CustomTooltip from '@/components/booking/bookingList/custom-tooltip.vue'
import BaseCheckbox from '@/components/_base-checkbox.vue'

export default {
  name: 'ClientFilterOption',
  components: { CustomTooltip, BaseCheckbox },
  props: {
    clientList: {
      type: Array,
      default: () => [],
    },
    selectedClient: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['toggle', 'apply-selected-client'],
  data() {
    return {
      showClient: false,
      searchClientQuery: '',
      localSelectedClientIds: [...this.selectedClient],
      scrollAreaHeight: 0,
    }
  },
  computed: {
    selectedClientCount() {
      return this.selectedClient.length
    },
    selectedClients() {
      return this.clientList
        .filter((client) =>
          this.localSelectedClientIds.includes(client.clientId)
        )
        .sort((a, b) => a.clientName.localeCompare(b.clientName))
    },
    unselectedClients() {
      return this.clientList
        .filter(
          (client) => !this.localSelectedClientIds.includes(client.clientId)
        )
        .sort((a, b) => a.clientName.localeCompare(b.clientName))
    },
    filteredSelectedClients() {
      return this.selectedClients.filter((client) =>
        (client.clientName || '')
          .toLowerCase()
          .includes((this.searchClientQuery || '').toLowerCase())
      )
    },
    filteredUnselectedClients() {
      return this.unselectedClients.filter((client) =>
        (client.clientName || '')
          .toLowerCase()
          .includes((this.searchClientQuery || '').toLowerCase())
      )
    },
  },
  watch: {
    selectedClient: {
      immediate: true,
      handler(newVal) {
        this.localSelectedClientIds = [...newVal]
      },
    },
    showClient(val) {
      if (val) {
        this.$nextTick(() => {
          this.updateScrollAreaHeight()
        })
      }
    },
  },
  created() {
    this.updateScrollAreaHeight = _.throttle(this.updateScrollAreaHeight, 100)
  },
  mounted() {
    document.addEventListener(
      'click',
      this.handleClickOutsideClientFilter,
      true
    )
    window.addEventListener('resize', this.updateScrollAreaHeight)
  },
  beforeDestroy() {
    document.removeEventListener('resize', this.updateScrollAreaHeight, true)
  },
  beforeUnmount() {
    document.removeEventListener(
      'click',
      this.handleClickOutsideClientFilter,
      true
    )
  },
  methods: {
    handleClickOutsideClientFilter(e) {
      const refsToCheck = ['clientContainer']

      const path =
        typeof e.composedPath === 'function'
          ? e.composedPath()
          : (function fallbackPath(n) {
              const p = []
              while (n) {
                p.push(n)
                n = n.parentNode || n.host
              }
              p.push(window)
              return p
            })(e.target)

      const clickedInsideRef = refsToCheck.some((ref) => {
        const el = this.$refs[ref]
        return el && path.includes(el)
      })

      const clickedOnTrigger = path.some((node) => {
        if (!(node instanceof HTMLElement)) return false
        return node.closest('.client-filter')
      })

      const inOverlay = path.some(
        (node) =>
          node instanceof HTMLElement &&
          (node.closest('.v-menu__content') ||
            node.closest('.v-overlay__content'))
      )

      if (!clickedInsideRef && !clickedOnTrigger && !inOverlay) {
        this.showClient = false
      }
    },
    toggleClientFilter() {
      this.showClient = !this.showClient
      this.$emit('toggle')
    },
    toggleClientSelection(clientId, isChecked) {
      const index = this.localSelectedClientIds.indexOf(clientId)
      if (isChecked && index === -1) {
        this.localSelectedClientIds.push(clientId)
      } else if (!isChecked && index !== -1) {
        this.localSelectedClientIds.splice(index, 1)
      }
    },
    hideAllSelectedClients() {
      const selectedIds = this.filteredSelectedClients.map((c) => c.clientId)
      this.localSelectedClientIds = this.localSelectedClientIds.filter(
        (id) => !selectedIds.includes(id)
      )
    },
    showAllUnselectedClients() {
      const unselectedIds = this.filteredUnselectedClients.map(
        (c) => c.clientId
      )
      this.localSelectedClientIds = Array.from(
        new Set([...this.localSelectedClientIds, ...unselectedIds])
      )
    },
    updateScrollAreaHeight() {
      this.$nextTick(() => {
        const container = this.$refs.clientContainer
        if (!container) return

        const topOffset = container.getBoundingClientRect().top
        const viewportHeight = window.innerHeight
        const marginBottom = 40

        this.scrollAreaHeight = viewportHeight - topOffset - marginBottom
      })
    },
    emitSelection() {
      this.$emit('apply-selected-client', [...this.localSelectedClientIds])
      this.showClient = false
    },
  },
}
</script>

<template>
  <div style="position: relative">
    <!-- Toggle button -->
    <CustomTooltip
      position="bottom"
      class="client-filter"
      :class="showClient ? 'active' : ''"
      :text="$t('topFilterBar.client.title')"
    >
      <div class="cursor-pointer" @click.stop="toggleClientFilter">
        <v-icon class="me-2">mdi-account-outline</v-icon>
        <span class="top-filter-title">
          {{
            $tc('topFilterBar.client.showClientsTilte', selectedClientCount, {
              count: selectedClientCount,
            })
          }}
        </span>
      </div>
    </CustomTooltip>

    <!-- Dropdown panel -->
    <div
      v-if="showClient"
      ref="clientContainer"
      class="client-container"
      :style="{ maxHeight: scrollAreaHeight + 'px' }"
    >
      <div class="title px-2 mb-3">{{ $t('topFilterBar.client.title') }}</div>

      <!-- Search box -->
      <div class="filter-container-search mb-5">
        <v-icon>mdi-magnify</v-icon>
        <input
          v-model="searchClientQuery"
          class="ps-2 search-input h-full"
          style="width: 100%; outline: none"
          :placeholder="$t('client.clientSelectorSearchInputText')"
        />
      </div>

      <div class="client-scrollable">
        <!-- Selected section -->
        <div
          v-if="filteredSelectedClients.length > 0"
          class="d-flex px-2"
          style="justify-content: space-between"
        >
          <span class="client-showall cursor-pointer">{{
            $t('topFilterBar.client.showing')
          }}</span>
          <span
            class="client-hideall cursor-pointer"
            @click="hideAllSelectedClients"
          >
            {{ $t('topFilterBar.client.hideAll') }}
          </span>
        </div>

        <div v-if="filteredSelectedClients.length > 0" class="mt-5">
          <div
            v-for="client in filteredSelectedClients"
            :key="'selected-' + client.clientId"
            class="mb-1 clients-list d-flex items-start space-x-2"
          >
            <BaseCheckbox
              :value="client.clientId"
              :model-value="localSelectedClientIds.includes(client.clientId)"
              sm
              custom-class="custom-checkbox"
              @update:modelValue="
                (isChecked) => toggleClientSelection(client.clientId, isChecked)
              "
            />
            <span
              class="client-name"
              style="font-weight: 500"
              @click="
                toggleClientSelection(
                  client.clientId,
                  !localSelectedClientIds.includes(client.clientId)
                )
              "
            >
              {{ client.clientName }}
            </span>
          </div>
        </div>

        <!-- Unselected section -->
        <div
          v-if="filteredUnselectedClients.length > 0"
          class="d-flex mb-5 px-2"
          style="justify-content: space-between"
        >
          <span class="client-showall cursor-pointer">{{
            $t('topFilterBar.client.hidden')
          }}</span>
          <span
            class="client-hideall cursor-pointer"
            @click="showAllUnselectedClients"
          >
            {{ $t('topFilterBar.client.showAll') }}
          </span>
        </div>

        <div v-if="filteredUnselectedClients.length > 0" class="mt-5">
          <div
            v-for="client in filteredUnselectedClients"
            :key="'unselected-' + client.clientId"
            class="mb-1 clients-list d-flex items-start space-x-2"
          >
            <BaseCheckbox
              :value="client.clientId"
              :model-value="false"
              sm
              custom-class="custom-checkbox"
              @update:modelValue="
                (isChecked) => toggleClientSelection(client.clientId, isChecked)
              "
            />
            <span
              class="client-name"
              style="font-weight: 500"
              @click="toggleClientSelection(client.clientId, true)"
            >
              {{ client.clientName }}
            </span>
          </div>
        </div>
      </div>

      <!-- Apply Button -->
      <div class="client-footer">
        <BaseButton
          :border-radius="'8px'"
          :width="'70px'"
          :height="'30px'"
          class="apply-btn p-5"
          @click.stop="emitSelection"
        >
          {{ $t('apply') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>
